import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

// Local row types used for analytics calculations
type SubscriptionPlanRef = {
  duration?: number;
  duration_type?: string;
  name?: string;
};

type SubscriptionRow = {
  price?: number;
  currency_code?: string;
  promo_discount?: number;
  referral_discount?: number;
  total_discount?: number;
  status?: string;
  subscription_start?: string;
  subscription_end?: string | null;
  created_at?: string;
  subscription_plan?: SubscriptionPlanRef;
};

type CollaboratorEarningRow = {
  collaborator_id?: number | string;
  amount_earned?: number;
  promotion?: { id?: number } | null;
  collaborator?: { name?: string } | null;
};

// Types for business analytics
export interface DateRange {
  from: Date;
  to: Date;
}

export interface OverviewAnalytics {
  activeSubscriptions: number;
  expiredSubscriptionsCount?: number;
  subscriptionPlansCount: number;
  subscriptionPlansInactiveCount?: number;
  promotionsActiveCount?: number;
  promotionsInactiveCount?: number;
  actualRevenue: number;
  revenueGrowth: number;
  churnRate: number;
  lifetimeGrossRevenue: number;
  lifetimeGrossRevenueInr?: number;
  lifetimeGrossRevenueUsd?: number;
  historicalTrends: Array<{
    month: string;
    totalMRR: number;
    newMRR: number;
    churnedMRR: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
    totalSubscriptions: number;
    inrRevenue?: number; // raw INR revenue in bucket
    usdRevenue?: number; // raw USD revenue in bucket
  }>;
  planPerformance: Array<{
    name: string;
    revenue: number;
    subscriptions: number;
    avgPrice: number;
    growth: number;
  }>;
  // Last 30 days daily trends for detailed charting
  dailyTrends?: Array<{
    day: string; // e.g., "Sep 1"
    inrRevenue: number;
    usdRevenue: number;
    newSubscriptions: number;
    totalSubscriptions: number;
  }>;
}

export interface RevenueAnalytics {
  grossRevenue: number;
  actualRevenue: number;
  referralDiscounts: number;
  promoDiscounts: number;
  revenueGrowthRate: number;
  subscribersCount?: number;
  inrRevenue?: number;
  usdRevenue?: number;
  planPerformance: Array<{
    name: string;
    revenue: number;
    subscriptions: number;
    avgPrice: number;
    growth: number;
    inrRevenue?: number; // monthly-equivalent revenue in INR currency (not FX-converted)
    usdRevenue?: number; // monthly-equivalent revenue in USD currency (not FX-converted)
  }>;
}

export interface SubscriptionAnalytics {
  activeSubscriptions: number;
  newSubscriptions: number;
  churnedSubscriptions: number;
  churnRate: number;
  subscriptionPlansCount: number;
  // Revenue metrics
  currentMRR: number;
  actualCashFlow: number; // Total money received in period
  // Historical trends with both actual cash and MRR
  historicalTrends: Array<{
    month: string;
    totalSubscriptions: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
    actualCashReceived: number; // What customers actually paid that month
    totalMRR: number; // Monthly recurring revenue calculation
    newMRR: number; // MRR from new subscriptions
    churnedMRR: number; // MRR lost from churned subscriptions
    inrRevenue?: number; // raw INR revenue in bucket
    usdRevenue?: number; // raw USD revenue in bucket
  }>;
  recentSubscribers: Array<{
    id: number;
    name: string;
    email: string;
    subscriptionDate: string;
    plan?: string;
  }>;
}

export interface ReferralCollaborationAnalytics {
  referralDiscounts: number;
  collaboratorCommissions: number;
  pendingPayouts: number;
  totalCollaborators: number;
  totalReferrals: number;
  totalReferralRewards: number;
  referralTrends: Array<{
    month: string;
    newReferrals: number;
    rewardsGranted: number;
    totalRewards: number;
  }>;
  collaboratorPerformance: Array<{
    name: string;
    earnings: number;
    promotions: number;
    conversions: number;
    revenue: number;
  }>;
  activePromotions: Array<{
    id: number;
    name: string;
    description?: string;
    code: string;
    discount_value: number;
    discount_type: string;
    current_uses: number;
    max_uses?: number;
    is_active: boolean;
  }>;
}

// Helper function to calculate revenue metrics
const calculateRevenue = (subscriptionsData: SubscriptionRow[]) => {
  let actualRevenue = 0;
  let totalReferralDiscounts = 0;
  let totalPromoDiscounts = 0;
  
  if (subscriptionsData && subscriptionsData.length > 0) {
    subscriptionsData.forEach((subscription: SubscriptionRow) => {
      const plan = subscription.subscription_plan;
      
      let subscriptionPrice = subscription.price || 0;
      if (subscription.currency_code === "USD") {
        subscriptionPrice = subscriptionPrice * 85;
      }

      // For revenue calculation, we should use the full subscription price, not monthly price
      // The monthly price calculation is only needed for MRR calculations
      actualRevenue += subscriptionPrice;

      let referralDiscount = 0;
      let promoDiscount = 0;

      if (subscription.referral_discount && subscription.referral_discount > 0) {
        referralDiscount = subscription.referral_discount;
        if (subscription.currency_code === "USD") {
          referralDiscount = referralDiscount * 85;
        }
        totalReferralDiscounts += referralDiscount;
      }

      if (subscription.promo_discount && subscription.promo_discount > 0) {
        promoDiscount = subscription.promo_discount;
        if (subscription.currency_code === "USD") {
          promoDiscount = promoDiscount * 85;
        }
        totalPromoDiscounts += promoDiscount;
      }
    });
  }

  return {
    actualRevenue: Math.round(actualRevenue * 100) / 100,
    totalReferralDiscounts: Math.round(totalReferralDiscounts * 100) / 100,
    totalPromoDiscounts: Math.round(totalPromoDiscounts * 100) / 100,
    grossRevenue: Math.round((actualRevenue + totalReferralDiscounts + totalPromoDiscounts) * 100) / 100,
  };
};

// Helper function to generate plan performance
const generatePlanPerformance = (subscriptionsData: SubscriptionRow[]) => {
  const planMap = new Map<string, {
    name: string;
    revenue: number; // combined, INR-normalized using FX
    subscriptions: number;
    avgPrice: number;
    inrRevenue: number; // native INR
    usdRevenue: number; // native USD
  }>();
  
  subscriptionsData?.forEach((sub: SubscriptionRow) => {
    const planName = sub.subscription_plan?.name || "Unknown Plan";
    if (!planMap.has(planName)) {
      planMap.set(planName, {
        name: planName,
        revenue: 0,
        subscriptions: 0,
        avgPrice: 0,
        inrRevenue: 0,
        usdRevenue: 0,
      });
    }
    
    const plan = planMap.get(planName);
    // Use full subscription price, not monthly equivalent
    const subscriptionPrice = sub.price || 0;
    
    // Add to native currency splits
    if (sub.currency_code === "USD") {
      plan.usdRevenue += subscriptionPrice;
    } else {
      plan.inrRevenue += subscriptionPrice;
    }
    // Combined, FX-normalized to INR for consistency
    const fxPriceInInr = sub.currency_code === "USD" ? subscriptionPrice * 85 : subscriptionPrice;
    plan.revenue += fxPriceInInr;
    plan.subscriptions += 1;
    plan.avgPrice = plan.revenue / plan.subscriptions;
    planMap.set(planName, plan);
  });
  
  return Array.from(planMap.values()).map(plan => ({
    ...plan,
    revenue: Math.round(plan.revenue),
    avgPrice: Math.round(plan.avgPrice),
    inrRevenue: Math.round(plan.inrRevenue * 100) / 100,
    usdRevenue: Math.round(plan.usdRevenue * 100) / 100,
    growth: Math.floor(Math.random() * 20) + 5 // Mock growth for now
  }));
};

// Helper function to generate historical trends constrained to a date range (inclusive)
const generateHistoricalTrends = (
  subscriptionsInRange: SubscriptionRow[],
  rangeFrom: Date,
  rangeTo: Date
) => {
  const trends: Array<{
    month: string;
    totalSubscriptions: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
    actualCashReceived: number;
    totalMRR: number;
    newMRR: number;
    churnedMRR: number;
    inrRevenue?: number;
    usdRevenue?: number;
  }> = [];
  
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const totalDays = Math.max(1, Math.ceil((rangeTo.getTime() - rangeFrom.getTime()) / MS_PER_DAY));
  const useDaily = totalDays <= 31;

  // Build buckets (daily or monthly)
  const bucketStarts: Date[] = [];
  if (useDaily) {
    let cursor = new Date(rangeFrom.getFullYear(), rangeFrom.getMonth(), rangeFrom.getDate());
    const endDay = new Date(rangeTo.getFullYear(), rangeTo.getMonth(), rangeTo.getDate());
    while (cursor <= endDay) {
      bucketStarts.push(new Date(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    }
  } else {
    const start = new Date(rangeFrom.getFullYear(), rangeFrom.getMonth(), 1);
    const end = new Date(rangeTo.getFullYear(), rangeTo.getMonth(), 1);
    let cursor = new Date(start);
    while (cursor <= end) {
      bucketStarts.push(new Date(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
  }

  let cumulativeTotalSubscriptions = 0;
  let cumulativeMRR = 0;

  bucketStarts.forEach((bucketStart: Date) => {
    const bucketEnd = useDaily
      ? new Date(bucketStart.getFullYear(), bucketStart.getMonth(), bucketStart.getDate(), 23, 59, 59, 999)
      : new Date(bucketStart.getFullYear(), bucketStart.getMonth() + 1, 0, 23, 59, 59, 999);

  const bucketSubs =
      subscriptionsInRange?.filter((sub: SubscriptionRow) => {
        const subDate = new Date(sub.subscription_start || sub.created_at);
        // Exclude cancelled
        const notCancelled = sub.status !== "cancelled";
        return notCancelled && subDate >= bucketStart && subDate <= bucketEnd;
      }) || [];

    let actualCashReceived = 0;
    let inrRevenueBucket = 0;
    let usdRevenueBucket = 0;
    let newMRR = 0;
    let newSubscriptions = 0;
    let churnedSubscriptions = 0; // Without an end date field, we cannot reliably bucket churn by day/month

    bucketSubs.forEach((sub: SubscriptionRow) => {
      const isNew = sub.status === "active"; // Approximation: new subs are active at start
      const isChurn = sub.status === "expired"; // treat only expired as churn in chart; cancelled excluded

      if (isNew) {
        newSubscriptions++;
        let actualPrice = sub.price || 0;
        if (sub.currency_code === "USD") {
          usdRevenueBucket += actualPrice;
        } else {
          inrRevenueBucket += actualPrice;
        }
        if (sub.currency_code === "USD") actualPrice *= 85;
        actualCashReceived += actualPrice;

        let mrrEquivalent = actualPrice;
        const plan = sub.subscription_plan;
        if (plan) {
          if (plan.duration_type === "months" && plan.duration > 1) {
            mrrEquivalent = actualPrice / plan.duration;
          } else if (plan.duration_type === "years") {
            mrrEquivalent = actualPrice / (plan.duration * 12);
          } else if (plan.duration_type === "days" && plan.duration > 30) {
            mrrEquivalent = actualPrice / (plan.duration / 30);
          }
        }
        newMRR += mrrEquivalent;
      }

      if (isChurn) {
        churnedSubscriptions++;
      }
    });

    cumulativeTotalSubscriptions = Math.max(
      0,
      cumulativeTotalSubscriptions + newSubscriptions - churnedSubscriptions
    );
    cumulativeMRR = Math.max(0, cumulativeMRR + newMRR); // Without churn timing, we don't subtract here

    trends.push({
      month: useDaily ? format(bucketStart, "MMM d") : format(bucketStart, "MMM"),
      totalSubscriptions: cumulativeTotalSubscriptions,
      newSubscriptions,
      churnedSubscriptions,
      actualCashReceived: Math.round(actualCashReceived),
      totalMRR: Math.round(cumulativeMRR),
      newMRR: Math.round(newMRR),
      churnedMRR: 0, // Unknown without churn timestamps
      inrRevenue: Math.round(inrRevenueBucket * 100) / 100,
      usdRevenue: Math.round(usdRevenueBucket * 100) / 100,
    });
  });

  return trends;
};

// API: Overview Analytics
export const fetchOverviewAnalytics = async (dateRange?: DateRange): Promise<OverviewAnalytics> => {
  try {
    const useRange = !!dateRange;
    const fromDate = (dateRange?.from || new Date(0)).toISOString();
    const toDate = (dateRange?.to || new Date()).toISOString();

    const [
      { count: subscriptionCount },
      { count: expiredSubscriptionsCount },
      { count: subscriptionPlansCount },
      { count: subscriptionPlansInactiveCount },
      { count: promotionsActiveCount },
      { count: promotionsInactiveCount },
      { data: subscriptionsData },
      { data: allSubscriptions },
      { data: allSubsForLifetime }
    ] = await Promise.all([
      // Active subscriptions (current), not limited by date if overview
      (useRange
        ? supabase
            .from("user_subscriptions")
            .select("*", { count: "exact", head: true })
            .eq("status", "active")
            .lte("subscription_start", toDate)
            .or(`subscription_end.is.null,subscription_end.gt.${toDate}`)
        : supabase
            .from("user_subscriptions")
            .select("*", { count: "exact", head: true })
            .eq("status", "active")
            .lte("subscription_start", new Date().toISOString())
            .or(`subscription_end.is.null,subscription_end.gt.${new Date().toISOString()}`)
      ),
      supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .or(
          `status.eq.expired,and(status.eq.active,subscription_end.lt.${new Date().toISOString()})`
        ),
      supabase.from("subscription_plans").select("*", { count: "exact", head: true }),
      supabase.from("subscription_plans").select("*", { count: "exact", head: true }).eq("is_active", false),
      supabase.from("promotions").select("*", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("promotions").select("*", { count: "exact", head: true }).eq("is_active", false),
      // Revenue calculation source
      (useRange
        ? supabase.from("user_subscriptions").select(`
            price, currency_code, promo_discount, referral_discount, total_discount,
            subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
              duration, duration_type, name
            )
          `)
            .not("status", "in", "(cancelled)")
            .gte("created_at", fromDate)
            .lte("created_at", toDate)
        : supabase.from("user_subscriptions").select(`
            price, currency_code, promo_discount, referral_discount, total_discount,
            subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
              duration, duration_type, name
            )
          `)
            .not("status", "in", "(cancelled)")
      ),
      // Historical trends: last 12 months regardless of range
      supabase.from("user_subscriptions").select(`
        subscription_start, created_at, status, price, currency_code,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(name, duration, duration_type)
      `)
        .not("status", "in", "(cancelled)")
        .gte("created_at", new Date(new Date().getTime() - 365 * 24 * 60 * 60 * 1000).toISOString()),
      // Lifetime gross revenue: exclude cancelled subscriptions
      supabase
        .from("user_subscriptions")
        .select("price, currency_code")
        .neq("status", "cancelled")
    ]);

    const revenueMetrics = calculateRevenue(subscriptionsData || []);
    const planPerformance = generatePlanPerformance(subscriptionsData || []);
    // Period currency split (raw amounts, not normalized)
    let periodInr = 0;
    let periodUsd = 0;
    (subscriptionsData || []).forEach((sub: SubscriptionRow) => {
      const price = sub.price || 0;
      if (sub.currency_code === "USD") {
        periodUsd += price;
      } else {
        periodInr += price;
      }
    });
    // Build a fixed 12-month window for overview trends (not affected by external filters)
    const now = new Date();
    const trendsFrom = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const trendsTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const historicalTrends = generateHistoricalTrends(
      allSubscriptions || [],
      trendsFrom,
      trendsTo
    );
    // Build last 30 days daily currency split and subscription growth
    const last30To = new Date();
    const last30From = new Date(last30To.getFullYear(), last30To.getMonth(), last30To.getDate() - 29);
    const { data: subsLast30 } = await supabase
      .from("user_subscriptions")
      .select(`
        subscription_start, created_at, status, price, currency_code,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(name, duration, duration_type)
      `)
      .neq("status", "cancelled")
      .gte("created_at", last30From.toISOString())
      .lte("created_at", last30To.toISOString());
    // Bucket daily
    const dailyTrends: Array<{ day: string; inrRevenue: number; usdRevenue: number; newSubscriptions: number; totalSubscriptions: number; }> = [];
    const dayCursor = new Date(last30From.getFullYear(), last30From.getMonth(), last30From.getDate());
    const endDay = new Date(last30To.getFullYear(), last30To.getMonth(), last30To.getDate());
    let cumulativeTotalSubscriptions = 0;
    while (dayCursor <= endDay) {
      const dayStart = new Date(dayCursor);
      const dayEnd = new Date(dayCursor.getFullYear(), dayCursor.getMonth(), dayCursor.getDate(), 23, 59, 59, 999);
      const subsForDay = (subsLast30 || []).filter((sub: SubscriptionRow) => {
        const subDate = new Date(sub.subscription_start || sub.created_at);
        return subDate >= dayStart && subDate <= dayEnd;
      });
      let inrRevenue = 0;
      let usdRevenue = 0;
      let newSubscriptions = 0;
      subsForDay.forEach((sub: SubscriptionRow) => {
        // Exclude cancelled from both revenue and counts
        if (sub.status === "cancelled") return;
        if (sub.status === "active") newSubscriptions += 1;
        const price = Number(sub.price || 0);
        if (sub.currency_code === "USD") {
          usdRevenue += price;
        } else {
          inrRevenue += price;
        }
      });
      cumulativeTotalSubscriptions = Math.max(0, cumulativeTotalSubscriptions + newSubscriptions);
      dailyTrends.push({
        day: format(dayStart, "MMM d"),
        inrRevenue: Math.round(inrRevenue * 100) / 100,
        usdRevenue: Math.round(usdRevenue * 100) / 100,
        newSubscriptions,
        totalSubscriptions: cumulativeTotalSubscriptions,
      });
      // advance
      dayCursor.setDate(dayCursor.getDate() + 1);
    }
    // Compute lifetime gross revenue in INR
    let lifetimeInr = 0;
    let lifetimeUsd = 0;
    (allSubsForLifetime || []).forEach((sub: SubscriptionRow) => {
      const price = sub.price || 0;
      if (sub.currency_code === "USD") {
        lifetimeUsd += price;
      } else {
        lifetimeInr += price;
      }
    });
    const lifetimeGrossRevenue = lifetimeInr + lifetimeUsd * 85;

    return {
      activeSubscriptions: subscriptionCount || 0,
      expiredSubscriptionsCount: expiredSubscriptionsCount || 0,
      subscriptionPlansCount: subscriptionPlansCount || 0,
      subscriptionPlansInactiveCount: subscriptionPlansInactiveCount || 0,
      promotionsActiveCount: promotionsActiveCount || 0,
      promotionsInactiveCount: promotionsInactiveCount || 0,
      actualRevenue: revenueMetrics.actualRevenue,
      revenueGrowth: 8.2, // Mock - would need historical data
      churnRate: 3.2, // Mock - would need historical data
      lifetimeGrossRevenue: Math.round(lifetimeGrossRevenue * 100) / 100,
      lifetimeGrossRevenueInr: Math.round(lifetimeInr * 100) / 100,
      lifetimeGrossRevenueUsd: Math.round(lifetimeUsd * 100) / 100,
      historicalTrends,
      planPerformance,
      dailyTrends,
    };
  } catch (error) {
    console.error("Error fetching overview analytics:", error);
    throw error;
  }
};

// API: Revenue Analytics
export const fetchRevenueAnalytics = async (dateRange?: DateRange): Promise<RevenueAnalytics> => {
  try {
    const defaultDateRange = {
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      to: new Date()
    };
    
    const effectiveDateRange = dateRange || defaultDateRange;
    const fromDate = effectiveDateRange.from.toISOString();
    const toDate = effectiveDateRange.to.toISOString();

    const { data: subscriptionsData } = await supabase.from("user_subscriptions").select(`
      price, currency_code, promo_discount, referral_discount, total_discount,
      subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
        duration, duration_type, name
      )
    `)
      .not("status", "in", "(cancelled)")
      .gte("created_at", fromDate)
      .lte("created_at", toDate);

    const revenueMetrics = calculateRevenue(subscriptionsData || []);
    const planPerformance = generatePlanPerformance(subscriptionsData || []);
    // Period currency split (raw amounts, not normalized)
    let periodInr = 0;
    let periodUsd = 0;
    (subscriptionsData || []).forEach((sub: SubscriptionRow) => {
      const price = sub.price || 0;
      if (sub.currency_code === "USD") {
        periodUsd += price;
      } else {
        periodInr += price;
      }
    });

    return {
      grossRevenue: revenueMetrics.grossRevenue,
      actualRevenue: revenueMetrics.actualRevenue,
      referralDiscounts: revenueMetrics.totalReferralDiscounts,
      promoDiscounts: revenueMetrics.totalPromoDiscounts,
      revenueGrowthRate: 8.2, // Mock - would need historical data
      subscribersCount: subscriptionsData?.length || 0,
      inrRevenue: Math.round(periodInr * 100) / 100,
      usdRevenue: Math.round(periodUsd * 100) / 100,
      planPerformance,
    };
  } catch (error) {
    console.error("Error fetching revenue analytics:", error);
    throw error;
  }
};

// API: Subscription Analytics
export const fetchSubscriptionAnalytics = async (dateRange?: DateRange): Promise<SubscriptionAnalytics> => {
  try {
    const defaultDateRange = {
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      to: new Date()
    };
    
    const effectiveDateRange = dateRange || defaultDateRange;
    const fromDate = effectiveDateRange.from.toISOString();
    const toDate = effectiveDateRange.to.toISOString();

    const [
      { count: subscriptionCount },
      { count: subscriptionPlansCount },
      { data: recentSubscribers },
      { data: subsForRange },
      { data: activeAsOfRangeEnd }
    ] = await Promise.all([
      // Active subscriptions started within the selected period
      supabase
        .from("user_subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active")
        .gte("subscription_start", fromDate)
        .lte("subscription_start", toDate),
      supabase
        .from("subscription_plans")
        .select("*", { count: "exact", head: true }),
      // Recent subscribers in the selected period
      supabase
        .from("user_subscriptions")
        .select(`
          id, user_id, subscription_start, created_at,
          subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(id, name),
          profile:profiles!user_subscriptions_user_id_fkey(id, name, email)
        `)
        .eq("status", "active")
        .gte("subscription_start", fromDate)
        .lte("subscription_start", toDate)
        .order("subscription_start", { ascending: false })
        .limit(10),
      // All subscriptions that started within the selected period for trends & cash flow
      supabase
        .from("user_subscriptions")
        .select(`
          subscription_start, created_at, status, price, currency_code,
          subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(name, duration, duration_type)
        `)
        .gte("subscription_start", fromDate)
        .lte("subscription_start", toDate)
        .neq("status", "cancelled"),
      // Active subscriptions as of the end of the period (time-bounded)
      supabase
        .from("user_subscriptions")
        .select(`
          price, currency_code,
          subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(duration, duration_type)
        `)
        .eq("status", "active")
        .lte("subscription_start", toDate)
        .or(`subscription_end.is.null,subscription_end.gt.${toDate}`)
    ]);

    // Calculate current MRR from all active subscriptions
    let currentMRR = 0;
    let totalCashFlowInPeriod = 0;

    if (activeAsOfRangeEnd && activeAsOfRangeEnd.length > 0) {
      activeAsOfRangeEnd.forEach((sub) => {
        let price = sub.price || 0;
        if (sub.currency_code === "USD") price *= 85;
        
        // Calculate MRR equivalent
        let mrrEquivalent = price;
        const plan = sub.subscription_plan;
        if (plan) {
          if (plan.duration_type === "months" && plan.duration > 1) {
            mrrEquivalent = price / plan.duration;
          } else if (plan.duration_type === "years") {
            mrrEquivalent = price / (plan.duration * 12);
          } else if (plan.duration_type === "days" && plan.duration > 30) {
            mrrEquivalent = price / (plan.duration / 30);
          }
        }
        currentMRR += mrrEquivalent;
      });
    }

    // Calculate actual cash flow for the selected period (assume payment at start)
    const subscriptionsInPeriod = subsForRange || [];

    subscriptionsInPeriod
      .filter((sub) => sub.status !== "cancelled")
      .forEach((sub) => {
        let price = sub.price || 0;
        if (sub.currency_code === "USD") price *= 85;
        totalCashFlowInPeriod += price;
      });

    const historicalTrends = generateHistoricalTrends(
      subsForRange || [],
      effectiveDateRange.from,
      effectiveDateRange.to
    );
    const recentSubscribersData = recentSubscribers?.map((sub) => ({
      id: sub.id,
      name: sub.profile?.name || "Unknown User",
      email: sub.profile?.email || "",
      subscriptionDate: sub.subscription_start || sub.created_at,
      plan: sub.subscription_plan?.name
    })) || [];

    // Calculate new/churned subscriptions for the selected period
    const newSubscriptions = (subsForRange || []).filter((s) => s.status === "active").length;
    const churnedSubscriptions = (subsForRange || []).filter(
      (s) => s.status === "cancelled" || s.status === "expired"
    ).length;

    return {
      activeSubscriptions: subscriptionCount || 0,
      newSubscriptions,
      churnedSubscriptions,
      churnRate: newSubscriptions + churnedSubscriptions > 0
        ? Math.round(((churnedSubscriptions / Math.max(1, newSubscriptions + churnedSubscriptions)) * 100) * 10) / 10
        : 0,
      subscriptionPlansCount: subscriptionPlansCount || 0,
      currentMRR: Math.round(currentMRR * 100) / 100,
      actualCashFlow: Math.round(totalCashFlowInPeriod * 100) / 100,
      historicalTrends,
      recentSubscribers: recentSubscribersData,
    };
  } catch (error) {
    console.error("Error fetching subscription analytics:", error);
    throw error;
  }
};

// API: Referral & Collaboration Analytics
export const fetchReferralCollaborationAnalytics = async (dateRange?: DateRange): Promise<ReferralCollaborationAnalytics> => {
  try {
    const defaultDateRange = {
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      to: new Date()
    };
    
    const effectiveDateRange = dateRange || defaultDateRange;
    const fromDate = effectiveDateRange.from.toISOString();
    const toDate = effectiveDateRange.to.toISOString();

    const [
      { data: subscriptionsData },
      { data: collaboratorEarnings },
      { data: collaboratorPayouts },
      { data: referralStats },
      { data: referralRewards },
      { data: activePromotions }
    ] = await Promise.all([
      supabase.from("user_subscriptions").select(`
        price, currency_code, promo_discount, referral_discount, total_discount,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          duration, duration_type, name
        )
      `)
        .not("status", "in", "(cancelled)")
        .gte("created_at", fromDate)
        .lte("created_at", toDate),
      supabase.from("collaborator_earnings").select(`
        id, collaborator_id, amount_earned, created_at,
        collaborator:collaborator_id(id, name, email),
        promotion:promotion_id(id, name, code)
      `).gte("created_at", fromDate)
        .lte("created_at", toDate)
        .order("created_at", { ascending: false }),
      supabase.from("collaborator_payouts").select("*")
        .gte("created_at", fromDate)
        .lte("created_at", toDate),
      supabase.from("referral_stats").select(`
        id, referrer_id, referred_user_id, created_at, reward_amount,
        referrer:referrer_id(name),
        referred_user:referred_user_id(name)
      `).gte("created_at", fromDate)
        .lte("created_at", toDate)
        .order("created_at", { ascending: false }),
      supabase.from("referral_rewards").select("*")
        .gte("created_at", fromDate)
        .lte("created_at", toDate)
        .order("created_at", { ascending: false }),
      supabase.from("promotions").select("*").eq("is_active", true)
    ]);

    const revenueMetrics = calculateRevenue(subscriptionsData || []);
    
    // Calculate collaborator metrics
    const totalCollaboratorEarnings = collaboratorEarnings?.reduce((sum, earning) => 
      sum + (earning.amount_earned || 0), 0) || 0;
    
    const pendingPayouts = collaboratorPayouts?.filter(p => p.status === "pending")
      .reduce((sum, payout) => sum + (payout.amount || 0), 0) || 0;

    // Calculate referral metrics
    const totalReferralRewards = referralRewards?.reduce((sum, reward) => 
      sum + (reward.reward_amount || 0), 0) || 0;

    // Generate collaborator performance
    const collaboratorMap = new Map<number | string, {
      name: string;
      earnings: number;
      promotions: Set<number>;
      conversions: number;
      revenue: number;
    }>();
    collaboratorEarnings?.forEach((earning: CollaboratorEarningRow) => {
      const collaboratorId = earning.collaborator_id;
      const collaborator = earning.collaborator;
      
      if (!collaborator) return;
      
      if (!collaboratorMap.has(collaboratorId)) {
        collaboratorMap.set(collaboratorId, {
          name: collaborator.name || "Unknown Collaborator",
          earnings: 0,
          promotions: new Set(),
          conversions: 0,
          revenue: 0
        });
      }
      
      const collabStats = collaboratorMap.get(collaboratorId as number | string)!;
      collabStats.earnings += earning.amount_earned || 0;
      collabStats.conversions += 1;
      collabStats.revenue += (earning.amount_earned || 0) * 4;
      
      if (earning.promotion?.id) {
        collabStats.promotions.add(earning.promotion.id);
      }
    });
    
    const collaboratorPerformance = Array.from(collaboratorMap.values())
      .map(collab => ({
        ...collab,
        promotions: collab.promotions.size,
        earnings: Math.round(collab.earnings),
        revenue: Math.round(collab.revenue)
      }))
      .sort((a, b) => b.earnings - a.earnings)
      .slice(0, 10);

    // Generate referral trends
  const referralTrends: Array<{ month: string; newReferrals: number; rewardsGranted: number; totalRewards: number; }> = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      
      const monthReferrals = referralStats?.filter(ref => {
        const refDate = new Date(ref.created_at);
        return refDate >= month && refDate <= monthEnd;
      }) || [];

      const monthRewards = referralRewards?.filter(reward => {
        const rewardDate = new Date(reward.created_at);
        return rewardDate >= month && rewardDate <= monthEnd;
      }) || [];

      referralTrends.push({
        month: format(month, "MMM"),
        newReferrals: monthReferrals.length,
        rewardsGranted: monthRewards.length,
        totalRewards: monthRewards.reduce((sum, reward) => sum + (reward.reward_amount || 0), 0)
      });
    }

    return {
      referralDiscounts: revenueMetrics.totalReferralDiscounts,
      collaboratorCommissions: Math.round(totalCollaboratorEarnings * 100) / 100,
      pendingPayouts: Math.round(pendingPayouts * 100) / 100,
      totalCollaborators: new Set(collaboratorEarnings?.map(e => e.collaborator_id)).size,
      totalReferrals: referralStats?.length || 0,
      totalReferralRewards: Math.round(totalReferralRewards * 100) / 100,
      referralTrends,
      collaboratorPerformance,
      activePromotions: activePromotions || [],
    };
  } catch (error) {
    console.error("Error fetching referral collaboration analytics:", error);
    throw error;
  }
}; 
 
// API: Monthly Growth Trends (churn = cancelled + expired). Dedicated for Monthly Growth Analysis chart
export const fetchMonthlyGrowthTrends = async (): Promise<Array<{ month: string; newSubscriptions: number; churnedSubscriptions: number }>> => {
  try {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 11, 1).toISOString();
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();

    const { data: subs } = await supabase
      .from("user_subscriptions")
      .select(`subscription_start, created_at, status`)
      .gte("created_at", from)
      .lte("created_at", to);

    type MinimalSubRow = { subscription_start?: string; created_at?: string; status?: string };
    const subsSafe: MinimalSubRow[] = (subs as MinimalSubRow[]) || [];
    const trends: Array<{ month: string; newSubscriptions: number; churnedSubscriptions: number }> = [];
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    let cursor = new Date(start);
    while (cursor < end) {
      const bucketStart = new Date(cursor);
      const bucketEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);
      const inBucket = subsSafe.filter((s: MinimalSubRow) => {
        const d = new Date(s.subscription_start || s.created_at || "");
        return d >= bucketStart && d <= bucketEnd;
      });
      const newSubscriptions = inBucket.filter((s: MinimalSubRow) => s.status === "active").length;
      const churnedSubscriptions = inBucket.filter((s: MinimalSubRow) => s.status === "cancelled" || s.status === "expired").length;
      trends.push({ month: format(bucketStart, "MMM"), newSubscriptions, churnedSubscriptions });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return trends;
  } catch (error) {
    console.error("Error fetching monthly growth trends:", error);
    throw error;
  }
};
 